/** Reducer for the watermark library (VE-24693). Mirrors settingsProfile. */
import { sanitizeStoredImageDataUrl } from '@redact-modules/documents/watermarkImage';
import {
  clampWatermarkOpacity,
  normalizeWatermarkColor,
} from '@redact-modules/documents/models';
import { createReducer } from '@reduxjs/toolkit';
import { unionBy } from 'lodash';
import * as Actions from '../actions';
import { StoredWatermarkDefinition, WatermarkDefinition } from '../models';
import { defaultState, namespace, WatermarksState } from '../store';

/**
 * Vet one record on its way into the store.
 *
 * These records come from an org-shared SDO registry that any org member can
 * write through the public GraphQL API — not only through this UI — and the
 * library table renders them DIRECTLY, without going through the watermark
 * factories that normalize an applied instance. So the two fields that reach a
 * DOM sink are cleaned here, at the one boundary, rather than at each of the
 * five places that render one:
 *
 *  - `imageBaseDataUrl` goes into an `<img src>`; a remote URL there turns
 *    every colleague's settings page into a callback to someone else's server.
 *  - `color` is interpolated into a CSS declaration value, which neither MUI
 *    nor emotion escapes, so a `}` in it closes the rule and opens one with an
 *    attacker-chosen selector. See {@link normalizeWatermarkColor}.
 *
 * A record that fails keeps its name and type and simply has nothing to draw,
 * which is the degradation we want: the entry stays visible and deletable.
 *
 * Applied to every case that writes `watermarkList`, not just the fetch. The
 * create/update echoes are the client's own payload coming back, so they are
 * not a live attack path today — but "one validated boundary" is only true if
 * it has no side doors, and the next writer will copy whichever case they read.
 */
function vetRecord(rec: WatermarkDefinition): StoredWatermarkDefinition {
  return {
    ...rec,
    raw: rec,
    data: {
      ...rec.data,
      imageBaseDataUrl:
        sanitizeStoredImageDataUrl(rec.data?.imageBaseDataUrl) ?? undefined,
      // Vetted exactly like the raster, and for the same reason: since §K this
      // is the image every list surface actually renders, so it is now the
      // field most likely to reach an `<img src>` on this page. Being small is
      // not a safety property — a uniform 20000x20000 PNG is a few KB on the
      // wire and ~1.6GB decoded, and the pixel check is what catches that.
      previewDataUrl:
        sanitizeStoredImageDataUrl(rec.data?.previewDataUrl) ?? undefined,
      // '' rather than a color: a text preview with no usable color falls
      // back to the table's own default, and an image tint of '' means
      // "untinted", which is the safe reading of an unusable value.
      colorHex: normalizeWatermarkColor(rec.data?.colorHex, ''),
      // The last CSS sink here that was going through unchecked. Theoretical
      // while the published schema types it as a number in 0..0.8 — but the
      // schema is the only thing enforcing that, this list exists precisely
      // because a record is not necessarily one this client wrote, and "the
      // server checks it" is what every one of the fields above could have
      // said too. AC#8 is also a product rule, not a rendering detail: a
      // watermark must never fully cover the text underneath.
      // Undefined stays undefined — the key is optional now, and a row that
      // never stored one must fall through to the factory's per-type default
      // rather than being pinned here to whatever `clampWatermarkOpacity`
      // returns for nothing.
      opacity:
        typeof rec.data?.opacity === 'number'
          ? clampWatermarkOpacity(rec.data.opacity)
          : undefined,
    },
  };
}

export const watermarksReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(Actions.FETCH_WATERMARKS_REQUEST, (state) => {
        state.isFetchInProgress = true;
      })
      .addCase(Actions.FETCH_WATERMARKS_SUCCESS, (state, action) => {
        const { records } = action.payload.structuredDataObjects;
        state.watermarkList = unionBy(records || [], [], 'id').map(vetRecord);
        state.isFetchInProgress = false;
        state.hasFetched = true;
      })
      .addCase(Actions.SET_WATERMARK_REGISTRY_UNAVAILABLE, (state, action) => {
        state.registryUnavailable = action.payload.unavailable;
      })
      .addCase(Actions.FETCH_WATERMARKS_FAILURE, (state) => {
        state.isFetchInProgress = false;
        // On failure too: the empty state means "your org has no watermarks",
        // which a failed request is no evidence of — but leaving the table in a
        // permanent loading limbo is worse than showing it once the attempt is
        // over, and the failure toast says what happened.
        state.hasFetched = true;
      })
      .addCase(Actions.CREATE_WATERMARK_REQUEST, (state) => {
        state.isSaveInProgress = true;
      })
      .addCase(Actions.CREATE_WATERMARK_SUCCESS, (state, action) => {
        state.watermarkList.push(
          vetRecord(action.payload.createStructuredData)
        );
        state.isSaveInProgress = false;
        state.isDrawerOpen = false;
        state.editingWatermarkId = undefined;
      })
      .addCase(Actions.CREATE_WATERMARK_FAILURE, (state) => {
        state.isSaveInProgress = false;
      })
      .addCase(Actions.UPDATE_WATERMARK_REQUEST, (state) => {
        state.isSaveInProgress = true;
      })
      .addCase(Actions.UPDATE_WATERMARK_SUCCESS, (state, action) => {
        const updated = vetRecord(action.payload.createStructuredData);
        state.watermarkList = state.watermarkList.map((w) =>
          w.id === updated.id ? updated : w
        );
        state.isSaveInProgress = false;
        state.isDrawerOpen = false;
        state.editingWatermarkId = undefined;
        // Soft delete is an upsert, so it reports back through THIS action
        // rather than DELETE_*. Without clearing the delete state here the
        // confirmation dialog stayed open and `isDeleteInProgress` stayed true
        // forever — the write had already succeeded and the row had already
        // left the table, so it read as a hang rather than a bug.
        //
        // But only for the row the dialog is actually about. Three writers
        // share this action — the Enabled toggle, restore and soft delete — and
        // they are all `takeEvery`, so they overlap: flip row A's switch, then
        // confirm row B's delete, and A's write lands first. Clearing on any
        // completion closed B's dialog and stopped its spinner while B's own
        // write was still in flight, telling the user a destructive action had
        // finished when it had not.
        if (updated.id === state.deletingWatermarkId) {
          state.isDeleteInProgress = false;
          state.deletingWatermarkId = undefined;
        }
      })
      .addCase(Actions.UPDATE_WATERMARK_FAILURE, (state) => {
        state.isSaveInProgress = false;
        // Deliberately leaves `deletingWatermarkId` set: the failure toast says
        // what happened, and the dialog staying open is what lets the user try
        // again. Only the in-flight flag clears, so the buttons come back.
        state.isDeleteInProgress = false;
      })
      .addCase(Actions.DELETE_WATERMARK_REQUEST, (state) => {
        state.isDeleteInProgress = true;
      })
      // The permanent delete raises the same flag, which it did not before —
      // so the dialog's spinner never engaged on the ONE action that cannot be
      // undone, while the reversible one had it. That inverts the reason the
      // spinner exists. It also matters more here: the saga is `takeEvery`, so
      // a second click on an unresponsive button issues a second
      // `deleteStructuredData`; the first succeeds and closes the dialog, the
      // second fails and raises a red toast saying a permanent delete failed
      // when it had in fact succeeded.
      .addCase(Actions.PERMANENTLY_DELETE_WATERMARK_REQUEST, (state) => {
        state.isDeleteInProgress = true;
      })
      .addCase(Actions.DELETE_WATERMARK_SUCCESS, (state, action) => {
        const id = action.payload?.deleteStructuredData?.id;
        state.watermarkList = state.watermarkList.filter((w) => w.id !== id);
        // Scoped to the row under confirmation, for the same reason as the
        // update case above: these writes overlap.
        if (id === state.deletingWatermarkId) {
          state.isDeleteInProgress = false;
          state.deletingWatermarkId = undefined;
        }
      })
      .addCase(Actions.DELETE_WATERMARK_FAILURE, (state) => {
        state.isDeleteInProgress = false;
      })
      .addCase(Actions.OPEN_WATERMARK_DRAWER, (state, action) => {
        state.isDrawerOpen = true;
        state.editingWatermarkId = action.payload.editingId;
      })
      .addCase(Actions.CLOSE_WATERMARK_DRAWER, (state) => {
        state.isDrawerOpen = false;
        state.editingWatermarkId = undefined;
      })
      .addCase(Actions.SET_SHOW_DELETED_WATERMARKS, (state, action) => {
        state.showDeleted = action.payload.show;
      })
      .addCase(Actions.SET_WATERMARK_ENABLED, (state, action) => {
        // Optimistic, because the switch is fully store-controlled: it does not
        // move until the upsert round-trips, so on a slow link the operator
        // clicks, sees nothing happen, and clicks again — two writes and a
        // final state that is whichever one landed last.
        //
        // Only the VETTED copy moves. `raw` is what the next write is built
        // from and must keep saying what the server actually holds, so a failed
        // toggle cannot leak a value the server never accepted into a later
        // write. UPDATE_WATERMARK_FAILURE refetches and truth returns.
        const row = state.watermarkList.find((w) => w.id === action.payload.id);
        if (row) {
          row.data.enabled = action.payload.enabled;
        }
      })
      .addCase(Actions.SET_WATERMARK_AUTO_APPLY, (state, action) => {
        // Optimistic, and only the VETTED copy — the same two properties the
        // Enabled toggle beside it relies on. `raw` keeps saying what the
        // server holds, so a failed write cannot leak a value it never accepted
        // into the next one; UPDATE_WATERMARK_FAILURE refetches.
        const row = state.watermarkList.find((w) => w.id === action.payload.id);
        if (row) {
          row.data.autoApply = action.payload.autoApply;
        }
      })
      .addCase(Actions.SET_WATERMARK_APPLYING, (state, action) => {
        const { definitionId, applying } = action.payload;
        const ids = state.applyingWatermarkIds.filter(
          (id) => id !== definitionId
        );
        state.applyingWatermarkIds = applying ? [...ids, definitionId] : ids;
      })
      .addCase(Actions.SET_DELETING_WATERMARK, (state, action) => {
        state.deletingWatermarkId = action.payload.id;
        state.isDeletePermanent = action.payload.permanent === true;
      });
  }),
};

export type { WatermarksState };
